import type {
    APICreateAgentPromptRequest,
    APIUpdateAgentPromptRequest,
} from '@visorpro/client/dist/RestClient/services/AgentPromptService'
import type { APIAgentPrompt } from '@visorpro/client/dist/types/APIAgentPrompt'
import { makeAutoObservable, runInAction } from 'mobx'
import { useEffect, useMemo } from 'react'
import { toast } from 'react-toastify'
import { visorPRORestClient } from '../util/api'
import type { Stores } from '../util/store'
import { observed } from '../util/observed-decorator'

export class AgentPromptsStore {
    public promptsById: Record<string, APIAgentPrompt> = {}
    public promptIds: string[] = []
    // The constant compiled into the API: the floor of the resolution chain,
    // shown as a read-only row so a prompt can be started from the real text.
    public baselinePrompt = ''
    public isFetched = false
    public isFetching = false
    public isUpdating = false

    constructor() {
        makeAutoObservable(this)
    }

    public get defaultPromptId() {
        return this.promptIds.find(id => this.promptsById[id]?.is_default)
    }

    @observed('isFetching')
    public async list() {
        try {
            const prompts = await visorPRORestClient.agentPrompt.getAgentPrompts()

            const promptsById: Record<string, APIAgentPrompt> = {}
            const promptIds: string[] = []

            prompts.items.forEach(prompt => {
                promptsById[prompt.id] = prompt
                promptIds.push(prompt.id)
            })

            runInAction(() => {
                this.promptsById = promptsById
                this.promptIds = promptIds
                this.isFetched = true
            })
        } catch (e) {
            toast.error(
                `Failed to load Vi 3.0 prompts: ${(e as Error).message}`,
            )
        }
    }

    public async loadBaseline() {
        try {
            const config = await visorPRORestClient.agent.getConfig()
            runInAction(() => {
                this.baselinePrompt = config.baseline_agent_prompt
            })
        } catch (e) {
            toast.error(`Failed to load the baseline: ${(e as Error).message}`)
        }
    }

    @observed('isUpdating')
    public async create(input: APICreateAgentPromptRequest) {
        if (!this.validate(input)) return

        try {
            const prompt =
                await visorPRORestClient.agentPrompt.createAgentPrompt(input)
            runInAction(() => {
                this.promptsById = {
                    ...this.promptsById,
                    [prompt.id]: prompt,
                }
                this.promptIds = [prompt.id, ...this.promptIds]
            })
            return prompt.id
        } catch (e) {
            toast.error(`Failed to create prompt: ${(e as Error).message}`)
        }
    }

    @observed('isUpdating')
    public async update(id: string, input: APIUpdateAgentPromptRequest) {
        if (!this.validate(input)) return

        try {
            const prompt =
                await visorPRORestClient.agentPrompt.updateAgentPrompt(
                    id,
                    input,
                )
            this.replace(prompt)
            return prompt.id
        } catch (e) {
            toast.error(`Failed to update prompt: ${(e as Error).message}`)
        }
    }

    @observed('isUpdating')
    public async delete(id: string) {
        try {
            await visorPRORestClient.agentPrompt.deleteAgentPrompt(id)
            runInAction(() => {
                const copy = { ...this.promptsById }
                delete copy[id]
                this.promptsById = copy
                this.promptIds = this.promptIds.filter(it => it !== id)
            })
            return true
        } catch (e) {
            toast.error(`Failed to delete prompt: ${(e as Error).message}`)
            return false
        }
    }

    @observed('isUpdating')
    public async setOrganizations(id: string, organizationIds: string[]) {
        try {
            const prompt =
                await visorPRORestClient.agentPrompt.setAgentPromptOrganizations(
                    id,
                    { organization_ids: organizationIds },
                )
            // Another prompt may have lost an organization to this one, so the
            // whole list is refetched rather than patched.
            this.replace(prompt)
            await this.list()
            return true
        } catch (e) {
            toast.error(
                `Failed to assign organizations: ${(e as Error).message}`,
            )
            return false
        }
    }

    @observed('isUpdating')
    public async promote(id: string) {
        try {
            await visorPRORestClient.agentPrompt.promoteAgentPrompt(id)
            // Promotion demotes another row, so the list is refetched.
            await this.list()
            return true
        } catch (e) {
            toast.error(`Failed to promote prompt: ${(e as Error).message}`)
            return false
        }
    }

    public promptForOrganization(organizationId: string) {
        return this.promptIds
            .map(id => this.promptsById[id])
            .find(prompt => prompt?.organizations.some(o => o.id === organizationId))
    }

    // The org-side edit, expressed as the prompt-side one so there is still a
    // single write path: the join's PK moves the org off its old prompt for us.
    public async setOrganizationPrompt(
        organizationId: string,
        promptId: string | undefined,
    ) {
        const current = this.promptForOrganization(organizationId)
        if (current?.id === promptId) return true

        if (!promptId) {
            if (!current) return true
            return this.setOrganizations(
                current.id,
                current.organizations
                    .map(o => o.id)
                    .filter(id => id !== organizationId),
            )
        }

        const target = this.promptsById[promptId]
        if (!target) return false
        return this.setOrganizations(promptId, [
            ...target.organizations.map(o => o.id),
            organizationId,
        ])
    }

    private replace = (prompt: APIAgentPrompt) => {
        runInAction(() => {
            this.promptsById = {
                ...this.promptsById,
                [prompt.id]: prompt,
            }
            if (!this.promptIds.includes(prompt.id)) {
                this.promptIds = [prompt.id, ...this.promptIds]
            }
        })
    }

    private validate = (
        input: APICreateAgentPromptRequest | APIUpdateAgentPromptRequest,
    ) => {
        if (input.name.trim().length === 0) {
            toast.error('Prompt name is required')
            return false
        }
        if (input.prompt.trim().length === 0) {
            toast.error('Prompt text is required')
            return false
        }
        return true
    }
}

export const useAgentPrompts = (stores: Stores) => {
    useEffect(() => {
        if (!stores.agentPrompts.isFetched) {
            void stores.agentPrompts.list()
        }
        if (!stores.agentPrompts.baselinePrompt) {
            void stores.agentPrompts.loadBaseline()
        }
    }, [stores.agentPrompts])

    return useMemo(() => {
        return stores.agentPrompts.promptIds.map(
            id => stores.agentPrompts.promptsById[id],
        )
    }, [stores.agentPrompts.promptIds, stores.agentPrompts.promptsById])
}
